import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemText,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ── 2025 (Canada-default) static data ───────────────────────────────────────

const SURVEY_STATS_2025 = [
    { label: '45,300+', description: 'Shoppers participated' },
    { label: '240,033', description: 'Brand evaluations' },
    { label: '363', description: 'Categories evaluated' },
];

const CATEGORY_SEGMENTS = [
    'Household & Cleaning',
    'Pet Care',
    'Home Goods & Outdoor',
    'Personal Care',
    'Health',
    'Beauty',
    'Food & Beverage',
    'Baby & Kids',
    'Retail & Restaurants',
    'Travel',
    'Banking',
    'Insurance',
    'Media',
    'Telecommunications',
];

const TRUST_DEPTH_SCALE = [
    'I trust much more than other brands',
    'I trust a little more than other brands',
    'I trust equally to some other brands',
    "I don't really trust, but I distrust less than other brands",
];

const BRAND_ATTRIBUTES = [
    'Offers superior quality products or services',
    'Offers the best quality for the price',
    'Is more likely to be recommended by others',
    'Is more open and honest with consumers',
    'Is more consistently reliable',
    'Provides superior customer support / service (if service-related)',
    'Has a longer or more established history in the category',
    'Is a leading innovator in the category',
    'Offers something distinct in the category',
    'Has a positive impact on society or the community',
    'I feel a stronger personal connection to this brand',
];

// ── 2026 per-country data ────────────────────────────────────────────────────

const COUNTRY_DATA_2026 = {
    US: {
        countryLabel: 'United States',
        title: 'BrandSpark Most Trusted Awards: Methodology Overview',
        stats: [
            { label: '35,200+', description: 'Shoppers participated' },
            { label: '182,000', description: 'Brand evaluations' },
            { label: '359', description: 'Categories evaluated' },
        ],
        categoryNomination:
            'BrandSpark researchers identify categories where trust is important to Americans in the following segments: household & cleaning, pet care, home goods & outdoor, personal care, health, beauty, food & beverage, baby & kids, retail & restaurants, and major services including travel, banking, insurance, media and telecommunications.',
        researchVoting: {
            fieldDates: 'September 12 – October 19, 2025',
            points: [
                'Respondents are recruited via personal invitation from multiple panel sources to ensure a broad sample representing a true cross-section of American adults.',
                'Shoppers are screened for participation in specific categories within the past 12 months to qualify to vote.',
                'Respondents can only contribute one response per category; there is no path for brands to direct respondents to the survey.',
                'The final sample is weighted to a standard national profile of shoppers.',
            ],
            unaidedVotes: true,
        },
        hasSurveyFlow: true,
    },
    CANADA: {
        countryLabel: 'Canada',
        title: '2026 BrandSpark Canadian Trust Survey: Methodology Overview',
        stats: [
            { label: '45,300+', description: 'Shoppers participated' },
            { label: '240,033', description: 'Brand evaluations' },
            { label: '363', description: 'Categories evaluated' },
        ],
        categoryNomination:
            'BrandSpark researchers identify categories where trust is important to Canadians. The 2026 edition included the following segments: household & cleaning, pet care, home goods & outdoor, personal care, health, beauty, food & beverage, baby & kids, retail & restaurants, and major services including travel, banking, insurance, media and telecommunications.',
        researchVoting: {
            fieldDates: 'September 2 – October 14, 2025',
            points: [
                'Respondents are recruited via personal invitation from multiple panel sources to ensure a broad sample representing a true cross-section of Canadian adults.',
                'Shoppers are screened for participation in specific categories within the past 12–24 months to qualify to vote.',
                'Respondents can only contribute one response per category; there is no path for brands to direct respondents to the survey.',
                'The final sample is weighted to a standard national profile of shoppers.',
                'Brand mentions are completely unaided, write-in votes — qualified consumers are asked which brand they trust most in particular categories.',
            ],
            unaidedVotes: false,
        },
        hasSurveyFlow: false,
    },
    UK: {
        countryLabel: 'United Kingdom',
        title: 'BrandSpark Most Trusted Awards: Methodology Overview',
        stats: [
            { label: '20,000+', description: 'Shoppers participated' },
            { label: '104,017', description: 'Brand evaluations' },
            { label: '166', description: 'Categories evaluated' },
        ],
        categoryNomination:
            'BrandSpark researchers identify categories where trust is important to UK shoppers in the following segments: household & cleaning, pet care, home goods & outdoor, personal care, health, beauty, food & beverage, baby & kids, retail & restaurants, and major services including travel, banking, insurance, media and telecommunications.',
        researchVoting: {
            fieldDates: 'October 2 – November 17, 2025',
            points: [
                'Respondents are recruited via personal invitation from multiple panel sources to ensure a broad sample representing a true cross-section of adults across the United Kingdom.',
                'Shoppers are screened for participation in specific categories within the past 12 months to qualify to vote.',
                'Respondents can only contribute one response per category; there is no path for brands to direct respondents to the survey.',
                'The final sample is weighted to a standard national profile of shoppers.',
            ],
            unaidedVotes: true,
        },
        hasSurveyFlow: true,
    },
};

// ── 2027 per-country data (UK only) ──────────────────────────────────────────
// TODO(2027): shopper count, brand evaluations and field dates are pending
// from BrandSpark (see 2027_questions.md); unknown stats are left out.

const COUNTRY_DATA_2027 = {
    UK: {
        countryLabel: 'United Kingdom',
        title: 'BrandSpark Most Trusted Awards: Methodology Overview',
        stats: [
            { label: '141', description: 'Categories evaluated' },
        ],
        categoryNomination:
            'BrandSpark researchers identify categories where trust is important to UK shoppers in the following segments: household & cleaning, pet care, home goods & outdoor, personal care, health, beauty, food & beverage, baby & kids, retail & restaurants, and major services including travel, banking, insurance, media and telecommunications.',
        researchVoting: {
            fieldDates: null,
            points: [
                'Respondents are recruited via personal invitation from multiple panel sources to ensure a broad sample representing a true cross-section of adults across the United Kingdom.',
                'Shoppers are screened for participation in specific categories within the past 12 months to qualify to vote.',
                'Respondents can only contribute one response per category; there is no path for brands to direct respondents to the survey.',
                'The final sample is weighted to a standard national profile of shoppers.',
                'Brand mentions are completely unaided, write-in votes — qualified consumers are asked which brand they trust most in particular categories.',
            ],
        },
        hasSurveyFlow: false,
    },
};

const COUNTRY_DATA_BY_YEAR = {
    2026: COUNTRY_DATA_2026,
    2027: COUNTRY_DATA_2027,
};

const BRAND_ATTRIBUTES_2026 = [
    'Offers superior quality products or services',
    'Offers the best quality for the price',
    'Is more likely to be recommended by others',
    'Is more open and honest with consumers',
    'Is more consistently reliable',
    'Provides superior customer support / service IF SERVICE RELATED',
    'Has a longer or more established history in the category',
    'Is a leading innovator in the category',
    'Offers something distinct in the category',
    'Has a positive impact on society or the community',
    'I feel a stronger personal connection to this brand',
];

const BRAND_ATTRIBUTES_2027 = [
    ...BRAND_ATTRIBUTES_2026,
    'Their products/services are fairly priced',
];

const BRAND_ATTRIBUTES_BY_YEAR = {
    2026: BRAND_ATTRIBUTES_2026,
    2027: BRAND_ATTRIBUTES_2027,
};

const SURVEY_FLOW_CATEGORY_SCREENER = [
    'Baby Goods',
    'Food & Beverage',
    'Personal Care / Grooming',
    'Skin Care & Beauty / Cosmetics',
    'Over-the-Counter/Non-Prescription Health',
    'Household Care (ex. Laundry, Cleaning, etc.)',
    'Pet Care',
    'Kitchenware / Appliances',
    'Household Electronics',
    'Other Home Goods',
    "Children's Products",
    'None of the above TERMINATE',
];

const SURVEY_FLOW_FOOD_PRODUCTS = [
    'Baking Yeast', 'Frozen Pizza', 'Peanut Butter', 'Canned Luncheon Meat',
    'Hazelnut Spread', 'Plant-Based Meat Alternatives', 'Chewing Gum', 'Jerky',
    'Protein Shake (premade)', 'Cooking Stock / Broth', 'Oat Plant-Based Beverage',
    'Ready-to-Eat Popcorn', 'Corn Starch', 'Olive Oil', 'Salad Kit',
    'Flavoured Water Enhancer', 'Pasta', 'White Bread', 'Fresh Fruits',
    'None of the above',
];

// ── Shared sub-components ────────────────────────────────────────────────────

const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
            {title}
        </Typography>
        {children}
    </Box>
);

// ── 2025 appendix (unchanged) ────────────────────────────────────────────────

const Methodology2025 = () => (
    <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The BrandSpark Most Trusted Awards (BMTAs) is the most comprehensive award program focused on consumer
            trust in brands. The methodology below describes how the 2026 edition of the survey was conducted.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {SURVEY_STATS_2025.map(({ label, description }) => (
                <Box
                    key={label}
                    sx={{
                        textAlign: 'center',
                        px: 3,
                        py: 1.5,
                        bgcolor: 'primary.50',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        borderRadius: 2,
                        minWidth: 120,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                </Box>
            ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Section title="1. Category Nomination">
            <Typography variant="body2" paragraph>
                BrandSpark researchers identify categories where trust is important to Canadians. The 2026 edition
                covered the following segments:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CATEGORY_SEGMENTS.map((seg) => (
                    <Chip key={seg} label={seg} size="small" variant="outlined" />
                ))}
            </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="2. Research &amp; Voting">
            <Typography variant="body2" paragraph>
                Polling of category shoppers is conducted via online survey. The 2026 edition was fielded{' '}
                <strong>September 2 – October 14, 2025</strong>.
            </Typography>
            <List dense disablePadding>
                {[
                    'Respondents are recruited via personal invitation from multiple panel sources to ensure a broad, nationally representative sample of Canadian adults.',
                    'Shoppers are screened for participation in specific categories within the past 12–24 months to qualify to vote.',
                    'Respondents can only contribute one response per category; there is no path for brands to direct respondents to the survey.',
                    'The final sample is weighted to a standard national profile of shoppers.',
                    'Brand mentions are completely unaided, write-in votes — qualified consumers are asked which brand they trust most in each category.',
                ].map((point, i) => (
                    <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                ))}
            </List>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="3. Winner Determination">
            <List dense disablePadding>
                {[
                    'The highest share of citations as "Most Trusted" brand determines the winner.',
                    'The margin of victory over the runner-up must exceed 3.0%; otherwise a tie is declared.',
                    'A minimum 10% trust share is required to be declared a winner.',
                    'Brands with 10%+ trust share ranking 2nd or 3rd in their category are declared "Top 3 Most Trusted".',
                ].map((point, i) => (
                    <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                ))}
            </List>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="4. Trust Depth Score">
            <Typography variant="body2" paragraph>
                Respondents rate how much they trust the brand versus others in the category:
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <List dense disablePadding>
                    {TRUST_DEPTH_SCALE.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText
                                primary={`${i + 1}. ${item}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="5. Brand Attribute Ratings">
            <Typography variant="body2" paragraph>
                Respondents indicate how much they agree with each statement about the brand compared to others in
                the category. Scale: <em>Strongly Agree, Agree, Neutral, Disagree, Strongly Disagree</em>.
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <List dense disablePadding>
                    {BRAND_ATTRIBUTES.map((attr, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`• ${attr}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="6. Open-Ended Questions">
            <List dense disablePadding>
                {[
                    { q: 'Trust reason', text: 'Why is that your most trusted brand in the category?' },
                    {
                        q: 'Personal connection (conditional)',
                        text: 'Shown only to respondents who strongly agree or agree that they have a stronger personal connection to the brand: "Please briefly describe an experience that created or confirmed that connection."',
                    },
                ].map(({ q, text }, i) => (
                    <ListItem key={i} sx={{ py: 0.5, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText
                            primary={q}
                            secondary={text}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                ))}
            </List>
        </Section>
    </>
);

// ── 2026 Canada / 2027 UK appendix (no Survey Flow section) ─────────────────

const Methodology2026Canada = ({ data, year = 2026, attributes = BRAND_ATTRIBUTES_2026 }) => (
    <>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {data.stats.map(({ label, description }) => (
                <Box
                    key={label}
                    sx={{
                        textAlign: 'center',
                        px: 3,
                        py: 1.5,
                        bgcolor: 'primary.50',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        borderRadius: 2,
                        minWidth: 120,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                </Box>
            ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Section title="1. Category Nomination">
            <Typography variant="body2" paragraph>{data.categoryNomination}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CATEGORY_SEGMENTS.map((seg) => (
                    <Chip key={seg} label={seg} size="small" variant="outlined" />
                ))}
            </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="2. Research &amp; Voting">
            <Typography variant="body2" paragraph>
                Polling of category shoppers is conducted via online survey.
                {data.researchVoting.fieldDates && (
                    <>
                        {' '}The {year} edition was fielded{' '}
                        <strong>{data.researchVoting.fieldDates}</strong>.
                    </>
                )}
            </Typography>
            <List dense disablePadding>
                {data.researchVoting.points.map((point, i) => (
                    <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                ))}
            </List>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="3. Winner Determination">
            <List dense disablePadding>
                {[
                    'The highest share of citations as "Most Trusted" brand determines the winner.',
                    'The margin of victory over the runner-up must exceed 3.0%; otherwise a tie is declared.',
                    'A minimum 10% trust share is required to be declared a winner.',
                    'Brands with 10%+ trust share ranking 2nd or 3rd in their category are declared "Top 3 Most Trusted".',
                ].map((point, i) => (
                    <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                ))}
            </List>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="4. Trust Depth Score">
            <Typography variant="body2" paragraph>
                The Trust depth score is determined by the following question:
            </Typography>
            <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
                On the scale below, please indicate to what degree you trust this brand versus others in this category:
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <List dense disablePadding>
                    {TRUST_DEPTH_SCALE.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="5. Open-Ended Question">
            <Typography variant="body2" paragraph>
                The open-ended question that follows is:{' '}
                <em>Why is that your most trusted brand in the category?</em>
            </Typography>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="6. Brand Attribute Ratings">
            <Typography variant="body2" paragraph>
                Respondents indicate how much they agree with each statement about the brand compared to others in
                the category. Scale: <em>Strongly Agree, Agree, Neutral, Disagree, Strongly Disagree</em>.
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <List dense disablePadding>
                    {attributes.map((attr, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`• ${attr}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Section>
    </>
);

// ── 2026 USA / UK appendix (includes Survey Flow section) ────────────────────

const Methodology2026WithSurveyFlow = ({ data }) => (
    <>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {data.stats.map(({ label, description }) => (
                <Box
                    key={label}
                    sx={{
                        textAlign: 'center',
                        px: 3,
                        py: 1.5,
                        bgcolor: 'primary.50',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        borderRadius: 2,
                        minWidth: 120,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                </Box>
            ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Section title="1. Category Nomination">
            <Typography variant="body2" paragraph>{data.categoryNomination}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CATEGORY_SEGMENTS.map((seg) => (
                    <Chip key={seg} label={seg} size="small" variant="outlined" />
                ))}
            </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="2. Research &amp; Voting">
            <Typography variant="body2" paragraph>
                Polling of category shoppers is conducted via online survey. The 2026 edition was fielded{' '}
                <strong>{data.researchVoting.fieldDates}</strong>.
            </Typography>
            <List dense disablePadding>
                {data.researchVoting.points.map((point, i) => (
                    <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                ))}
            </List>
            {data.researchVoting.unaidedVotes && (
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 2 }}>
                    <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                        Brand mentions are completely unaided, write-in votes:
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>PRODUCTS:</strong> Which brand of &lt;CATEGORY&gt; do you trust most? UNAIDED TEXT
                        <br />
                        I don't know EXCLUSIVE
                        <br /><br />
                        <strong>SERVICES:</strong> Which &lt;CATEGORY&gt; do you trust most? UNAIDED TEXT
                        <br />
                        I don't know EXCLUSIVE
                    </Typography>
                </Box>
            )}
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="3. Winner Determination">
            <List dense disablePadding>
                {[
                    'The highest share of citations as "Most Trusted" brand determines the winner.',
                    'The margin of victory over the runner-up must exceed 3.0%; otherwise a tie is declared.',
                    'A minimum 10% trust share is required to be declared a winner.',
                    'Brands with 10%+ trust share ranking 2nd or 3rd in their category are declared "Top 3 Most Trusted".',
                ].map((point, i) => (
                    <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start', pl: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                ))}
            </List>
        </Section>

        <Divider sx={{ mb: 3 }} />

        <Section title="Appendix: Survey Flow">
            <Typography variant="body2" paragraph>
                The following presents illustrative qualification and category questions for the example of 'Peanut Butter'.
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Category Screener
            </Typography>
            <Typography variant="body2" paragraph>
                In which categories do you have primary or shared responsibility for household purchases?
                Select all that apply. RANDOMIZE
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <List dense disablePadding>
                    {SURVEY_FLOW_CATEGORY_SCREENER.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Typography variant="body2" paragraph>
                <em>INode.</em> We will now ask multiple questions to confirm your participation in a list of product and service categories. You may qualify for many, but we will only ask detailed follow-up questions for a limited number of categories. Your responses will remain confidential.
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Category Qualification (example: Food &amp; Beverage)
            </Typography>
            <Typography variant="body2" paragraph>
                ASK IF SELECTED 'Food &amp; Beverage': Please indicate if you have purchased these types of Food products in the past 12 months. Please select all that apply. RANDOMIZE
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {SURVEY_FLOW_FOOD_PRODUCTS.map((item, i) => (
                        <Chip key={i} label={item} size="small" variant="outlined" />
                    ))}
                </Box>
            </Box>

            <Typography variant="body2" paragraph>
                Similar questions are shown for other segments qualified above. Assigned categories are presented in a random order as follows:
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Category Questions (example: Peanut Butter)
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" paragraph>
                    Which brand of Peanut Butter do you trust most? Please write just the name for one brand. OPEN
                    <br />
                    I don't know EXCLUSIVE, SKIP TO NEXT CATEGORY
                </Typography>
                <Typography variant="body2" paragraph>
                    On the scale below, please indicate to what degree you trust this brand versus others in this category:
                </Typography>
                <List dense disablePadding>
                    {TRUST_DEPTH_SCALE.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                    ))}
                </List>
                <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                    Why is that your most trusted brand in the category?
                    <br />
                    LARGE OPEN
                    <br />
                    I don't know EXCLUSIVE
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Brand Attribute Ratings
            </Typography>
            <Typography variant="body2" paragraph>
                Please indicate how much you agree with each statement regarding this brand compared to other brands in the category.
                SCALE: Strongly Agree, Agree, Neutral, Disagree, Strongly Disagree. ROWS, RANDOMIZE:
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <List dense disablePadding>
                    {BRAND_ATTRIBUTES_2026.map((attr, i) => (
                        <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`• ${attr}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Section>
    </>
);

// ── Main component ───────────────────────────────────────────────────────────

const MethodologyAppendix = ({ country, year, expanded, onChange }) => {
    const yearNum = Number(year);

    // Normalise country code: backend uses 'US' and 'CANADA' and 'UK'
    const countryKey = country ? country.toUpperCase() : null;
    const yearData = COUNTRY_DATA_BY_YEAR[yearNum];
    const data2026 = yearData && countryKey ? yearData[countryKey] || null : null;
    const attributes = BRAND_ATTRIBUTES_BY_YEAR[yearNum] || BRAND_ATTRIBUTES_2026;

    const title = data2026
        ? `Appendix: Survey Methodology — ${data2026.countryLabel}`
        : 'Appendix: Survey Methodology';

    const accordionProps = expanded !== undefined
        ? { expanded, onChange }
        : { defaultExpanded: false };

    return (
        <Accordion id="methodology-appendix" sx={{ mt: 4, border: '1px solid', borderColor: 'divider' }} {...accordionProps}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {data2026 ? (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {data2026.title}. The BrandSpark Most Trusted Awards (BMTAs) is the most comprehensive
                            award program focused on consumer trust in brands. The methodology below describes how the
                            {' '}{yearNum} edition of the survey was conducted.
                        </Typography>

                        {data2026.hasSurveyFlow ? (
                            <Methodology2026WithSurveyFlow data={data2026} />
                        ) : (
                            <Methodology2026Canada data={data2026} year={yearNum} attributes={attributes} />
                        )}
                    </>
                ) : (
                    <>
                        <Methodology2025 />
                    </>
                )}

                <Divider sx={{ mb: 2 }} />

                <Typography variant="caption" color="text.secondary">
                    Strictly Confidential – Not to be Distributed. Copyright © BrandSpark International 2026. All
                    Rights Reserved. BrandSpark Most Trusted, BrandSpark International, BNPA and Best New Product
                    Awards are registered trademarks.
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
};

export default MethodologyAppendix;
